import {
  ImageBlockEditing
} from "./chunk-MBUPU22L.js";
import "./chunk-5UJG77VP.js";
import {
  ImageUtils
} from "./chunk-MPGKEZT5.js";
import "./chunk-SA55FI2N.js";
import "./chunk-UHWQXN2Z.js";
import {
  toWidgetEditable
} from "./chunk-ZGNJRE43.js";
import "./chunk-6GJUXDIH.js";
import "./chunk-W7WGVHCV.js";
import "./chunk-NICE7ARI.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  ButtonView
} from "./chunk-CCHBLTQG.js";
import "./chunk-J3DZJUY2.js";
import {
  Command,
  Element,
  Plugin,
  enablePlaceholder,
  icons
} from "./chunk-XX3CIG5U.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-image/src/imagecaption/toggleimagecaptioncommand.js
var ToggleImageCaptionCommand = class extends Command {
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    const imageUtils = editor.plugins.get("ImageUtils");
    if (!editor.plugins.has(ImageBlockEditing)) {
      this.isEnabled = false;
      this.value = false;
      return;
    }
    const selection = editor.model.document.selection;
    const selectedElement = selection.getSelectedElement();
    if (!selectedElement) {
      const ancestorCaptionElement = imageCaptionUtils.getCaptionFromModelSelection(selection);
      this.isEnabled = !!ancestorCaptionElement;
      this.value = !!ancestorCaptionElement;
      return;
    }
    this.isEnabled = imageUtils.isImage(selectedElement);
    if (!this.isEnabled) {
      this.value = false;
    } else {
      this.value = !!imageCaptionUtils.getCaptionFromImageModelElement(selectedElement);
    }
  }
  /**
   * Executes the command.
   *
   * ```ts
   * editor.execute( 'toggleImageCaption' );
   * ```
   *
   * @param options Options for the executed command.
   * @param options.focusCaptionOnShow When true and the caption shows up, the selection will be moved into it straight away.
   * @fires execute
   */
  execute(options = {}) {
    const { focusCaptionOnShow } = options;
    this.editor.model.change((writer) => {
      if (this.value) {
        this._hideImageCaption(writer);
      } else {
        this._showImageCaption(writer, focusCaptionOnShow);
      }
    });
  }
  /**
   * Shows the caption of the `<imageBlock>` or `<imageInline>`. Also:
   *
   * * it converts `<imageInline>` to `<imageBlock>` to show the caption,
   * * it attempts to restore the caption content from the `ImageCaptionEditing` caption registry,
   * * it moves the selection to the caption right away, it the `focusCaptionOnShow` option was set.
   */
  _showImageCaption(writer, focusCaptionOnShow) {
    const model = this.editor.model;
    const selection = model.document.selection;
    const imageCaptionEditing = this.editor.plugins.get("ImageCaptionEditing");
    const imageUtils = this.editor.plugins.get("ImageUtils");
    let selectedImage = selection.getSelectedElement();
    const savedCaption = imageCaptionEditing._getSavedCaption(selectedImage);
    if (imageUtils.isInlineImage(selectedImage)) {
      this.editor.execute("imageTypeBlock");
      selectedImage = selection.getSelectedElement();
    }
    const newCaptionElement = savedCaption || writer.createElement("caption");
    writer.append(newCaptionElement, selectedImage);
    if (focusCaptionOnShow) {
      writer.setSelection(newCaptionElement, "in");
    }
  }
  /**
   * Hides the caption of a selected image (or an image caption the selection is anchored to).
   *
   * The content of the caption is stored in the `ImageCaptionEditing` caption registry to make this
   * a reversible action.
   */
  _hideImageCaption(writer) {
    const editor = this.editor;
    const selection = editor.model.document.selection;
    const imageCaptionEditing = editor.plugins.get("ImageCaptionEditing");
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    let selectedImage = selection.getSelectedElement();
    let captionElement;
    if (selectedImage) {
      captionElement = imageCaptionUtils.getCaptionFromImageModelElement(selectedImage);
    } else {
      captionElement = imageCaptionUtils.getCaptionFromModelSelection(selection);
      selectedImage = captionElement.parent;
    }
    imageCaptionEditing._saveCaption(selectedImage, captionElement);
    writer.setSelection(selectedImage, "on");
    writer.remove(captionElement);
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionutils.js
var ImageCaptionUtils = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageCaptionUtils";
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * Returns the caption model element from a given image element. Returns `null` if no caption is found.
   */
  getCaptionFromImageModelElement(imageModelElement) {
    for (const node of imageModelElement.getChildren()) {
      if (!!node && node.is("element", "caption")) {
        return node;
      }
    }
    return null;
  }
  /**
   * Returns the caption model element for a model selection. Returns `null` if the selection has no caption element ancestor.
   */
  getCaptionFromModelSelection(selection) {
    const imageUtils = this.editor.plugins.get("ImageUtils");
    const captionElement = selection.getFirstPosition().findAncestor("caption");
    if (!captionElement) {
      return null;
    }
    if (imageUtils.isBlockImage(captionElement.parent)) {
      return captionElement;
    }
    return null;
  }
  /**
   * {@link module:engine/view/matcher~Matcher} pattern. Checks if a given element is a `<figcaption>` element that is placed
   * inside the image `<figure>` element.
   * @returns Returns the object accepted by {@link module:engine/view/matcher~Matcher} or `null` if the element
   * cannot be matched.
   */
  matchImageCaptionViewElement(element) {
    const imageUtils = this.editor.plugins.get("ImageUtils");
    if (element.name == "figcaption" && imageUtils.isBlockImageView(element.parent)) {
      return { name: true };
    }
    return null;
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionediting.js
var ImageCaptionEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils, ImageCaptionUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageCaptionEditing";
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    this._savedCaptionsMap = /* @__PURE__ */ new WeakMap();
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const schema = editor.model.schema;
    if (!schema.isRegistered("caption")) {
      schema.register("caption", {
        allowIn: "imageBlock",
        allowContentOf: "$block",
        isLimit: true
      });
    } else {
      schema.extend("caption", {
        allowIn: "imageBlock"
      });
    }
    editor.commands.add("toggleImageCaption", new ToggleImageCaptionCommand(this.editor));
    this._setupConversion();
    this._setupImageTypeCommandsIntegration();
    this._registerCaptionReconversion();
  }
  /**
   * Configures conversion pipelines to support upcasting and downcasting
   * image captions.
   */
  _setupConversion() {
    const editor = this.editor;
    const view = editor.editing.view;
    const imageUtils = editor.plugins.get("ImageUtils");
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    const t = editor.t;
    editor.conversion.for("upcast").elementToElement({
      view: (element) => imageCaptionUtils.matchImageCaptionViewElement(element),
      model: "caption"
    });
    editor.conversion.for("dataDowncast").elementToElement({
      model: "caption",
      view: (modelElement, { writer }) => {
        if (!imageUtils.isBlockImage(modelElement.parent)) {
          return null;
        }
        return writer.createContainerElement("figcaption");
      }
    });
    editor.conversion.for("editingDowncast").elementToElement({
      model: "caption",
      view: (modelElement, { writer }) => {
        if (!imageUtils.isBlockImage(modelElement.parent)) {
          return null;
        }
        const figcaptionElement = writer.createEditableElement("figcaption");
        writer.setCustomProperty("imageCaption", true, figcaptionElement);
        figcaptionElement.placeholder = t("Enter image caption");
        enablePlaceholder({
          view,
          element: figcaptionElement,
          keepOnFocus: true
        });
        const imageAlt = modelElement.parent.getAttribute("alt");
        const label = imageAlt ? t("Caption for image: %0", [imageAlt]) : t("Caption for the image");
        return toWidgetEditable(figcaptionElement, writer, { label });
      }
    });
  }
  /**
   * Integrates with {@link module:image/image/imagetypecommand~ImageTypeCommand image type commands}
   * to make sure the caption is preserved when the type of an image changes so it can be restored
   * in the future if the user decides they want their caption back.
   */
  _setupImageTypeCommandsIntegration() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    const imageTypeInlineCommand = editor.commands.get("imageTypeInline");
    const imageTypeBlockCommand = editor.commands.get("imageTypeBlock");
    const handleImageTypeChange = (evt) => {
      if (!evt.return) {
        return;
      }
      const { oldElement, newElement } = evt.return;
      if (!oldElement) {
        return;
      }
      if (imageUtils.isBlockImage(oldElement)) {
        const oldCaptionElement = imageCaptionUtils.getCaptionFromImageModelElement(oldElement);
        if (oldCaptionElement) {
          this._saveCaption(newElement, oldCaptionElement);
          return;
        }
      }
      const savedOldElementCaption = this._getSavedCaption(oldElement);
      if (savedOldElementCaption) {
        this._saveCaption(newElement, savedOldElementCaption);
      }
    };
    if (imageTypeInlineCommand) {
      this.listenTo(imageTypeInlineCommand, "execute", handleImageTypeChange, { priority: "low" });
    }
    if (imageTypeBlockCommand) {
      this.listenTo(imageTypeBlockCommand, "execute", handleImageTypeChange, { priority: "low" });
    }
  }
  /**
   * Returns the saved {@link module:engine/model/element~Element#toJSON JSONified} caption
   * of an image model element.
   *
   * See {@link #_saveCaption}.
   *
   * @internal
   * @param imageModelElement The model element the caption should be returned for.
   * @returns The model caption element or `null` if there is none.
   */
  _getSavedCaption(imageModelElement) {
    const jsonObject = this._savedCaptionsMap.get(imageModelElement);
    return jsonObject ? Element.fromJSON(jsonObject) : null;
  }
  /**
   * Saves a {@link module:engine/model/element~Element#toJSON JSONified} caption for
   * an image element to allow restoring it in the future.
   *
   * A caption is saved every time it gets hidden and/or the type of an image changes. The
   * user should be able to restore it on demand.
   *
   * **Note**: The caption cannot be stored in the image model element attribute because,
   * for instance, when the model state propagates to collaborators, the attribute would get
   * lost (mainly because it does not convert to anything when the caption is hidden) and
   * the states of collaborators' models would de-synchronize causing numerous issues.
   *
   * See {@link #_getSavedCaption}.
   *
   * @internal
   * @param imageModelElement The model element the caption is saved for.
   * @param caption The caption model element to be saved.
   */
  _saveCaption(imageModelElement, caption) {
    this._savedCaptionsMap.set(imageModelElement, caption.toJSON());
  }
  /**
   * Reconverts image caption when image alt attribute changes.
   * The change of alt attribute is reflected in caption's aria-label attribute.
   */
  _registerCaptionReconversion() {
    const editor = this.editor;
    const model = editor.model;
    const imageUtils = editor.plugins.get("ImageUtils");
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    model.document.on("change:data", () => {
      const changes = model.document.differ.getChanges();
      for (const change of changes) {
        if (change.attributeKey !== "alt") {
          continue;
        }
        const image = change.range.start.nodeAfter;
        if (imageUtils.isBlockImage(image)) {
          const caption = imageCaptionUtils.getCaptionFromImageModelElement(image);
          if (!caption) {
            return;
          }
          editor.editing.reconvertItem(caption);
        }
      }
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionui.js
var ImageCaptionUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageCaptionUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageCaptionUI";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const editingView = editor.editing.view;
    const imageCaptionUtils = editor.plugins.get("ImageCaptionUtils");
    const t = editor.t;
    editor.ui.componentFactory.add("toggleImageCaption", (locale) => {
      const command = editor.commands.get("toggleImageCaption");
      const view = new ButtonView(locale);
      view.set({
        icon: icons.caption,
        tooltip: true,
        isToggleable: true
      });
      view.bind("isOn", "isEnabled").to(command, "value", "isEnabled");
      view.bind("label").to(command, "value", (value) => value ? t("Toggle caption off") : t("Toggle caption on"));
      this.listenTo(view, "execute", () => {
        editor.execute("toggleImageCaption", { focusCaptionOnShow: true });
        const modelCaptionElement = imageCaptionUtils.getCaptionFromModelSelection(editor.model.document.selection);
        if (modelCaptionElement) {
          const figcaptionElement = editor.editing.mapper.toViewElement(modelCaptionElement);
          editingView.scrollToTheSelection();
          editingView.change((writer) => {
            writer.addClass("image__caption_highlighted", figcaptionElement);
          });
        }
        editor.editing.view.focus();
      });
      return view;
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagecaption.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-image/theme/imagecaption.css";
var ImageCaption = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageCaptionEditing, ImageCaptionUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageCaption";
  }
};
export {
  ImageCaption as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-image/src/imagecaption/toggleimagecaptioncommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionutils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
  (* istanbul ignore if: paranoid check -- @preserve *)

@ckeditor/ckeditor5-image/src/imagecaption/imagecaptionui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagecaption.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-image_src_imagecaption.js.map
